import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import Loader from '../../../../common/components/Loader'
import {
  Currency,
  HeroButtonPayload,
  HeroSettings,
  HeroSettingsPayload,
  Languages,
  TicketCategory,
  TicketList,
} from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getCurrencies,
  getHeroSettings,
  updateHeroSettings,
} from '../../../../core/services/festival-settings'
import {
  getAllCategories,
  getTicketsByCategoryId,
} from '../../../../core/services/tickets'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import SelectField from '@/components/select-field'

interface IHeroSettingsValues {
  ticketButtonEnabled: boolean
  ticketButtonLink?: string | null
  extraButtonEnabled: boolean
  extraButtonLink?: string | null
  appStoreEnabled: boolean
  appStoreUrl?: string | null
  googleStoreEnabled: boolean
  googleStoreUrl?: string | null
  radioEnabled: boolean
  radioUrl?: string | null
  videoPortraitUrl: string
  videoLandscapeUrl: string
  ticketButtonNames?: HeroButtonValues[]
  extraButtonNames?: HeroButtonValues[]
  ticketCategory?: string | null
  ticketId?: string | null
  showFestivalDate: boolean
  showFestivalLocation: boolean
}

export interface HeroButtonValues {
  id?: string | null
  locale?: string
  text?: string
}

interface HeroSettingsProps {
  allLanguages: Languages[]
  hasEditPermission: boolean
}

export const HeroSettingsComp: React.FC<HeroSettingsProps> = (
  props: HeroSettingsProps,
) => {
  const { allLanguages, hasEditPermission } = props
  const { editionId, festivalName } = useParams()
  const [loading, setLoading] = useState(true)
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [tickets, setTickets] = useState<TicketList[]>([])
  const [ticketCategoriesData, setTicketCategoriesData] = useState<
    TicketCategory[]
  >([])
  const [hero, setHero] = useState<HeroSettings>()

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [price, setPrice] = useState<number | null>(null)

  const defaultLanguageContent = map(allLanguages, (lang) => {
    return {
      id: '',
      locale: lang.locale,
      text: '',
    }
  })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const [heroData, categoriesData, 
          currenciesData
        ] = await Promise.allSettled([
          getHeroSettings(),
          getAllCategories({ size: 1000 }),
          getCurrencies()
        ])

        if (heroData.status === 'fulfilled' && heroData.value.status === 200) {
          setHero(heroData.value.data)
          setValue('ticketButtonEnabled', !!heroData.value.data.ticketButtonEnabled)
          setValue('ticketButtonLink', heroData.value.data.ticketButtonLink)
          setValue('extraButtonEnabled', !!heroData.value.data.extraButtonEnabled)
          setValue('extraButtonLink', heroData.value.data.extraButtonLink)
          setValue('appStoreEnabled', heroData.value.data.appStoreEnabled)
          setValue('appStoreUrl', heroData.value.data.appStoreUrl)
          setValue('googleStoreEnabled', heroData.value.data.googleStoreEnabled)
          setValue('googleStoreUrl', heroData.value.data.googleStoreUrl)
          setValue('radioEnabled', heroData.value.data.radioEnabled)
          setValue('radioUrl', heroData.value.data.radioUrl)
          setValue('videoPortraitUrl', heroData.value.data.videoPortraitUrl)
          setValue('videoLandscapeUrl', heroData.value.data.videoLandscapeUrl)
          setValue('ticketCategory', heroData.value.data.ticket?.category?.id || '')
          setValue('showFestivalDate', heroData.value.data.showFestivalDate)
          setValue('showFestivalLocation', heroData.value.data.showFestivalLocation)
          if (heroData.value.data.ticket?.category.id) {
            getCategoryTickets(heroData.value.data.ticket?.category.id, false)
          }
          setValue('ticketId', heroData.value.data.ticket?.ticket?.id || '')
          setPrice(heroData.value.data.ticket?.price || null)

          setValue('ticketButtonNames', map(heroData.value.data.ticketButtonNames, (name) => {
            return {
              id: name.id,
              locale: name.locale,
              text: name.text,
            }
          }))

          setValue('extraButtonNames', map(heroData.value.data.extraButtonNames, (name) => {
            return {
              id: name.id,
              locale: name.locale,
              text: name.text,
            }
          }))

        }
        if (categoriesData.status === 'fulfilled' && categoriesData.value.status === 200) {
          setTicketCategoriesData(categoriesData.value.data.content)
        }
        if (currenciesData.status === 'fulfilled' && currenciesData.value.status === 200) {
          setCurrency(currenciesData.value.data.defaultCurrency)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape(
    {
      ticketButtonEnabled: yup.boolean().required('Field is required'),
      ticketButtonLink: yup.string().when('ticketButtonEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required('Field is required'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      ticketButtonNames: yup.array().of(
        yup.object().shape({
          id: yup.string().optional().nullable(),
          locale: yup.string().when('ticketButtonEnabled', {
            is: (val: boolean) => (val ? true : false),
            then: (schema) => schema.required('Field is required'),
            otherwise: (schema) => schema.optional().nullable(),
          }),
          text: yup.string().when('ticketButtonEnabled', {
            is: (val: boolean) => (val ? true : false),
            then: (schema) => schema.required('Field is required'),
            otherwise: (schema) => schema.optional().nullable(),
          }),
        }),
      ),
      appStoreEnabled: yup.boolean().required('Field is required'),
      appStoreUrl: yup.string().when('appStoreEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required('Field is required'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      googleStoreEnabled: yup.boolean().required('Field is required'),
      googleStoreUrl: yup.string().when('googleStoreEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required('Field is required'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      videoLandscapeUrl: yup.string().required('Field is required'),
      videoPortraitUrl: yup.string().required('Field is required'),
      radioEnabled: yup.boolean().required('Field is required'),
      radioUrl: yup.string().when('radioEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required('Field is required'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      extraButtonEnabled: yup.boolean().required('Field is required'),
      extraButtonLink: yup.string().when('extraButtonEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required('Field is required'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      extraButtonNames: yup.array().of(
        yup
          .object()
          .shape({
            id: yup.string().optional().nullable(),
            locale: yup.string().when('extraButtonEnabled', {
              is: (val: boolean) => (val ? true : false),
              then: (schema) => schema.required('Field is required'),
              otherwise: (schema) => schema.optional().nullable(),
            }),
            text: yup.string().when('extraButtonEnabled', {
              is: (val: boolean) => (val ? true : false),
              then: (schema) => schema.required('Field is required'),
              otherwise: (schema) => schema.optional().nullable(),
            }),
          })
          .when('extraButtonEnabled', {
            is: (val: boolean) => (val ? true : false),
            then: (schema) => schema.required('Field is required'),
            otherwise: (schema) => schema.optional().nullable(),
          }),
      ),
      ticketCategory: yup.string().optional().nullable(),
      ticketId: yup.string().optional().nullable(),
      showFestivalDate: yup.boolean().required('Field is required'),
      showFestivalLocation: yup.boolean().required('Field is required'),
    },
    [
      ['ticketButtonEnabled', 'ticketButtonLink'],
      ['ticketButtonEnabled', 'ticketButtonNames'],
      ['extraButtonEnabled', 'extraButtonLink'],
      ['extraButtonEnabled', 'extraButtonNames'],
      ['radioEnabled', 'radioUrl'],
      ['googleStoreEnabled', 'googleStoreUrl'],
      ['appStoreEnabled', 'appStoreUrl'],
    ],
  )

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<IHeroSettingsValues>({
    mode: 'onChange',
    resolver: yupResolver<IHeroSettingsValues>(schema),
    defaultValues: {
      extraButtonEnabled: false,
      ticketButtonEnabled: false,
      showFestivalDate: false,
      showFestivalLocation: false,
    },
  })

  const ticketButton = watch('ticketButtonEnabled')
  const extraButton = watch('extraButtonEnabled')
  const appStore = watch('appStoreEnabled')
  const googleStore = watch('googleStoreEnabled')
  const radio = watch('radioEnabled')
  const ticketCategoryWatch = watch('ticketCategory')
  const ticketIdWatch = watch('ticketId')

  const getCategoryTickets = async (
    category?: string,
    resetTicketValue?: boolean,
  ) => {
    try {
      setLoading(true)
      if (category) {
        const { data } = await getTicketsByCategoryId(
          category,
          { size: 1000 },
          false,
        )
        setTickets(data.content)
        if (resetTicketValue) {
          setValue('ticketId', '')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const submit = async (values: IHeroSettingsValues) => {
    let ticketButtonNames: HeroButtonPayload[] = defaultLanguageContent
    let extraButtonNames: HeroButtonPayload[] = defaultLanguageContent
    ticketButtonNames = values.ticketButtonEnabled
      ? map(values.ticketButtonNames, (name, index) => {
          return {
            id: name.id,
            locale: ticketButtonNames[index].locale,
            text: name.text || '',
          }
        })
      : []

    extraButtonNames = values.extraButtonEnabled
      ? map(values.extraButtonNames, (name, index) => {
          return {
            id: name.id,
            locale: extraButtonNames[index].locale,
            text: name.text || '',
          }
        })
      : []

    const data: HeroSettingsPayload = {
      ticketButtonEnabled: values.ticketButtonEnabled,
      ticketButtonLink: values.ticketButtonLink || '',
      extraButtonEnabled: values.extraButtonEnabled,
      extraButtonLink: values.extraButtonLink || '',
      appStoreEnabled: values.appStoreEnabled,
      appStoreUrl: values.appStoreUrl || '',
      googleStoreEnabled: values.googleStoreEnabled,
      googleStoreUrl: values.googleStoreUrl || '',
      radioEnabled: values.radioEnabled,
      radioUrl: values.radioUrl || '',
      videoPortraitUrl: values.videoPortraitUrl,
      videoLandscapeUrl: values.videoLandscapeUrl,
      ticketButtonNames: ticketButtonNames,
      extraButtonNames: extraButtonNames,
      showFestivalDate: values.showFestivalDate,
      showFestivalLocation: values.showFestivalLocation,
    }

    if (values.ticketId && values.ticketCategory) {
      data.ticket = {
        ticket: {
          id: values.ticketId,
          name: tickets.find((t) => t.id === values.ticketId)?.name || '',
        },
        category: {
          id: values.ticketCategory,
          name:
            ticketCategoriesData.find((t) => t.id === values.ticketCategory)
              ?.name || '',
        },
        festivalEdition: {
          id: editionId as string,
          name: festivalName as string,
        },
        price: price as number,
      }
    }

    try {
      setLoading(true)
      const { status } = await updateHeroSettings(data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (hero) {
      setValue('ticketButtonEnabled', hero.ticketButtonEnabled)
      setValue('ticketButtonLink', hero.ticketButtonLink)
      setValue('extraButtonEnabled', hero.extraButtonEnabled)
      setValue('extraButtonLink', hero.extraButtonLink)
      setValue('appStoreEnabled', hero.appStoreEnabled)
      setValue('appStoreUrl', hero.appStoreUrl)
      setValue('googleStoreEnabled', hero.googleStoreEnabled)
      setValue('googleStoreUrl', hero.googleStoreUrl)
      setValue('radioEnabled', hero.radioEnabled)
      setValue('radioUrl', hero.radioUrl)
      setValue('videoPortraitUrl', hero.videoPortraitUrl)
      setValue('videoLandscapeUrl', hero.videoLandscapeUrl)
      setValue('showFestivalDate', hero.showFestivalDate)
      setValue('showFestivalLocation', hero.showFestivalLocation)
      // setValue('ticketButtonNames', hero.ticketButtonNames)
      // setValue('extraButtonNames', hero.extraButtonNames)
    }
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div>
      <h4 className="text-title-s text-accent-1 mb-4">Hero Settings</h4>

      <Card className="p-4 mt-4">
        <form
          id="settings-hero-settings-form"
          className="space-y-7"
          onSubmit={handleSubmit(submit)}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col p-3 bg-monochrome-10 rounded-lg space-y-2">
                <FormGroup
                  label="Show Festival Date"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    checked={Boolean(watch('showFestivalDate'))}
                    onCheckedChange={(checked) =>
                      setValue('showFestivalDate', Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup
                  label="Show Festival Location"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    checked={Boolean(watch('showFestivalLocation'))}
                    onCheckedChange={(checked) =>
                      setValue('showFestivalLocation', Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup
                  label="Tickets Button"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    checked={Boolean(watch('ticketButtonEnabled'))}
                    onCheckedChange={(checked) =>
                      setValue('ticketButtonEnabled', Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup label="Tickets Button Link">
                  <Input
                    isInvalid={!!errors?.ticketButtonLink?.message}
                    {...register('ticketButtonLink')}
                    disabled={!ticketButton || !hasEditPermission}
                    placeholder="Enter Ticket Button Link"
                  />
                </FormGroup>
                <div className="flex flex-col gap-2 w-full">
                  {map(allLanguages, (language, index) => {
                    return (
                      <Card
                        className="flex flex-col p-3 gap-2 w-full"
                        key={language.id}
                      >
                        <FormGroup
                          label="Language:"
                          direction="row"
                          wrapperClassName="mb-0"
                        >
                          <span className="text-sm font-semibold text-accent-1">
                            {language.name}
                          </span>
                        </FormGroup>
                        <Input
                          {...register(`ticketButtonNames.${index}.id`)}
                          type="text"
                          className="hidden"
                          value={
                            watch(`ticketButtonNames.${index}.id`) ||
                            language.id
                          }
                          readOnly
                        />
                        <Input
                          {...register(`ticketButtonNames.${index}.locale`)}
                          type="text"
                          className="hidden"
                          value={
                            watch(`ticketButtonNames.${index}.locale`) ||
                            language.locale
                          }
                          readOnly
                        />
                        <FormGroup label="Translation" wrapperClassName="mb-0">
                          <Input
                            disabled={!ticketButton || !hasEditPermission}
                            placeholder="Enter Translation"
                            isInvalid={
                              !!errors?.ticketButtonNames?.[index]?.text
                                ?.message
                            }
                            {...register(`ticketButtonNames.${index}.text`)}
                            type="text"
                          />
                        </FormGroup>
                      </Card>
                    )
                  })}
                </div>
              </div>
              <div className="flex flex-col p-3 bg-monochrome-10 rounded-lg space-y-2">
                <FormGroup
                  label="Extra Button"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    checked={Boolean(watch('extraButtonEnabled'))}
                    onCheckedChange={(checked) =>
                      setValue('extraButtonEnabled', Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup
                  label="Extra Button Link"
                  labelWrapperClassName="me-4"
                >
                  <Input
                    disabled={!extraButton || !hasEditPermission}
                    isInvalid={!!errors?.extraButtonLink?.message}
                    {...register('extraButtonLink')}
                    type="text"
                    placeholder="Enter Extra Button Link"
                  />
                </FormGroup>
                <div className="flex flex-col gap-2 w-full">
                  {map(allLanguages, (language, index) => {
                    return (
                      <Card
                        className="flex flex-col p-3 gap-2 w-full"
                        key={language.id}
                      >
                        <FormGroup
                          label="Language:"
                          direction="row"
                          wrapperClassName="mb-0"
                        >
                          <span className="text-sm font-semibold text-accent-1">
                            {language.name}
                          </span>
                        </FormGroup>
                        <FormGroup label="Translation" wrapperClassName="mb-0">
                          <Input
                            disabled={!extraButton || !hasEditPermission}
                            placeholder="Enter Translation"
                            isInvalid={
                              !!errors?.extraButtonNames?.[index]?.text?.message
                            }
                            {...register(`extraButtonNames.${index}.text`)}
                            type="text"
                          />
                        </FormGroup>
                      </Card>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <h5 className="mb-0 text-lg font-semibold text-accent-1">
                Homepage Get Tickets Bar
              </h5>
              <div className="flex flex-row gap-3 w-full items-center">
                <FormGroup label="Ticket Category">
                  <Controller
                    name="ticketCategory"
                    control={control}
                    render={({ field }) => (
                      <>
                        <SelectField
                          outline
                          error={errors?.ticketCategory?.message}
                          options={map(ticketCategoriesData, (ticket) => ({
                            value: ticket.id,
                            label: ticket.name,
                          }))}
                          value={field.value || ''}
                          onValueChange={(value) => {
                            setValue('ticketCategory', value, {
                              shouldValidate: true,
                            })
                            getCategoryTickets(value, true)
                          }}
                          disabled={!hasEditPermission}
                        />
                      </>
                    )}
                  />
                </FormGroup>
                <FormGroup label="Select Ticket">
                  <Controller
                    name="ticketId"
                    control={control}
                    render={({ field }) => (
                      <SelectField
                        outline
                        error={errors?.ticketId?.message}
                        options={map(tickets, (ticket) => ({
                          value: ticket.id,
                          label: ticket.name,
                        }))}
                        value={field.value || ''}
                        onValueChange={(value) => {
                          setValue('ticketId', value, {
                            shouldValidate: true,
                          })
                          if (value !== '') {
                            const temp = find(
                              tickets,
                              (ticket) => ticket.id === value,
                            )
                            setPrice(!!temp ? temp.price : null)
                          }
                        }}
                        disabled={
                          ticketCategoryWatch === undefined ||
                          ticketCategoryWatch === '' ||
                          !hasEditPermission
                        }
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup label="">
                  {ticketIdWatch !== undefined && ticketIdWatch !== '' ? (
                    <div className="flex mt-4 items-center h-[36px] min-w-[114px]">
                      <span className="text-sm font-normal text-monochrome-2">
                        Price:{' '}
                        <span className="text-accent-1">
                          {price} {currency?.symbol}
                        </span>
                      </span>
                    </div>
                  ) : (
                    <div className="min-w-[114px] w-[114px] h-[36px]" />
                  )}
                </FormGroup>
              </div>
            </div>

            <FormGroup
              label="App Store Link"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                checked={Boolean(watch('appStoreEnabled'))}
                onCheckedChange={(checked) =>
                  setValue('appStoreEnabled', Boolean(checked))
                }
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="App Store">
              <Input
                disabled={!appStore || !hasEditPermission}
                isInvalid={!!errors?.appStoreUrl?.message}
                {...register('appStoreUrl')}
                type="text"
                placeholder="Enter App Store Link Url"
              />
            </FormGroup>
            <FormGroup
              label="Google Play Link"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                checked={Boolean(watch('googleStoreEnabled'))}
                onCheckedChange={(checked) =>
                  setValue('googleStoreEnabled', Boolean(checked))
                }
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Google Play">
              <Input
                disabled={!googleStore || !hasEditPermission}
                isInvalid={!!errors?.googleStoreUrl?.message}
                {...register('googleStoreUrl')}
                type="text"
                placeholder="Enter Google Play Link Url"
              />
            </FormGroup>
            <FormGroup label="Video Background for WEB">
              <Input
                isInvalid={!!errors?.videoLandscapeUrl?.message}
                {...register('videoLandscapeUrl')}
                type="text"
                disabled={!hasEditPermission}
                placeholder="Enter Video Background for WEB"
              />
            </FormGroup>
            <FormGroup label="Video Background for Mobile">
              <Input
                isInvalid={!!errors?.videoPortraitUrl?.message}
                {...register('videoPortraitUrl')}
                type="text"
                placeholder="Enter Video Background for Mobile"
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup
              label="Radio"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                checked={Boolean(watch('radioEnabled'))}
                onCheckedChange={(checked) =>
                  setValue('radioEnabled', Boolean(checked))
                }
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Radio Link">
              <Input
                disabled={!radio || !hasEditPermission}
                isInvalid={!!errors?.radioUrl?.message}
                {...register('radioUrl')}
                type="text"
                placeholder="Enter Radio Link"
              />
            </FormGroup>
          </div>
        </form>

        <div className="flex justify-end gap-2 p-3 mt-4 border-t border-monochrome-120">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-hero-settings-form"
            type="submit"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </div>
  )
}
